import { ContentArea } from '@calendly/ui/components/content-area';
import { RefObject, ReactNode, createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCenterPanelLayoutMode } from './centerPanelLayoutContext';

/**
 * Fade painted at the top of the scrollport. Absolutely positioned so it can never
 * take flex layout space under the utility hairline (was ~2rem / 32px even with
 * height:0 flex siblings).
 */
const filterGradientCss = "f1rl1b0m";

/*
 * Full-bleed wrapper reserving the scrollbar gutter, mirroring PageHeader's
 * sticky container so filter content aligns with the scroll region.
 */
const filterChromeCss = "fylgf4h";
const filterOutletScrollCss = "f13ycopn";

/** Edge-to-edge meetings chrome: never a horizontal scrollport — only inner chip strips scroll. */
const filterOutletContainCss = "f90z0b2";

// Exported so the legacy shell (LegacyPageFilterOutlet / legacy_app_layout) can
// reset these overrides via descendant selectors — see ADR 0001 and
// legacy_app_layout.js. Padding follows main's top-only fix: a bottom bleed
// overlapped the scroll fade but still painted empty space under the
// preset row
export const filterRendererBleedCss = "f1x617dx";

/**
 * Meetings phone: full chrome width, vertical pad only. Horizontal inset lives on
 * content rows (search / date+chips / count) so the utility hairline can be width
 * 100% with no negative margins — those expand max-content and make the whole
 * filter bar a horizontal scroller.
 */
const filterRendererEdgeToEdgeBleedCss = "f149ftlm";
export const filterRendererContentCss = "f1sitsyb";
const filterRendererEdgeToEdgeContentCss = "f1xo91qj";
type PageFilterContextType = {
  filterRef: RefObject<HTMLDivElement | null>;
  isRendered: boolean;
  refBound: boolean;
  edgeToEdge: boolean;
  setIsRendered: (isRendered: boolean) => void;
  setFilterRef: (ref: HTMLDivElement | null) => void;
  setEdgeToEdge: (edgeToEdge: boolean) => void;
};
const FilterContext = createContext<PageFilterContextType>({
  filterRef: {
    current: null
  },
  isRendered: false,
  refBound: false,
  edgeToEdge: false,
  setIsRendered: () => {},
  setFilterRef: () => {},
  setEdgeToEdge: () => {}
});
export const PageFilterContext = ({
  children
}: {
  children: ReactNode;
}) => {
  const filterRef = useRef<HTMLDivElement | null>(null);
  const [isRendered, setIsRendered] = useState(false);
  const [refBound, setRefBound] = useState(false);
  const [edgeToEdge, setEdgeToEdge] = useState(false);
  const setFilterRef = useCallback((ref: HTMLDivElement | null) => {
    setRefBound(!!ref);
    if (filterRef.current !== ref) {
      filterRef.current = ref;
    }
  }, []);
  const data = useMemo(() => ({
    filterRef,
    setFilterRef,
    isRendered,
    refBound,
    edgeToEdge,
    setIsRendered,
    setEdgeToEdge
  }), [isRendered, refBound, edgeToEdge, setFilterRef]);
  return <FilterContext.Provider value={data}>
      {children}
    </FilterContext.Provider>;
};
export const usePageFilterContext = () => useContext(FilterContext);

/**
 * Renders the filter slot in the top chrome of `CenterPanel`.
 * Mounts only when a `PageFilterRenderer` is active — no empty space otherwise.
 */
export const PageFilterOutlet = () => {
  const {
    setFilterRef,
    isRendered,
    edgeToEdge
  } = usePageFilterContext();
  const layout = useCenterPanelLayoutMode() ?? 'default';
  if (!isRendered) return null;
  const outlet = <div ref={setFilterRef} data-testid="page-filter-outlet" data-edge-to-edge={edgeToEdge ? 'true' : undefined} className={edgeToEdge ? filterOutletContainCss : filterOutletScrollCss} />;
  return <div className={filterChromeCss}>
      {edgeToEdge ? outlet : <ContentArea isWide={layout === 'wide'} padding="inline">
          {outlet}
        </ContentArea>}
    </div>;
};

/**
 * Renders a gradient overlay at the top of the scrollport when a filter is active.
 * Mount inside `#shell-content-scroll` (see `CenterPanelContent`) — absolute, so it
 * never consumes flex space under the utility hairline.
 */
export const PageFilterGradient = () => {
  const {
    isRendered,
    edgeToEdge
  } = usePageFilterContext();
  // Edge-to-edge meetings phone: sticky count owns Figma gaps; skip scroll fade spacer.
  if (!isRendered || edgeToEdge) return null;
  return <div aria-hidden className={filterGradientCss} />;
};
type PageFilterRendererProps = {
  children: ReactNode;
  /**
   * Full-chrome width (no ContentArea inset, no max-content scrollport). Page rows
   * own horizontal padding; use for meetings phone so the utility hairline can span
   * edge-to-edge while only the chip strip scrolls.
   */
  edgeToEdge?: boolean;
};

/**
 * Portals children into the `PageFilterOutlet` in the top chrome.
 * Place this at the top of a page component to hoist filters out of the scroll container.
 *
 * @example
 * function MyPage() {
 *   return (
 *     <>
 *       <PageFilterRenderer>
 *         <MyFilterBar />
 *       </PageFilterRenderer>
 *       <PageContent />
 *     </>
 *   )
 * }
 */
export const PageFilterRenderer = ({
  children,
  edgeToEdge = false
}: PageFilterRendererProps) => {
  const {
    filterRef,
    refBound,
    setIsRendered,
    setEdgeToEdge
  } = usePageFilterContext();
  useLayoutEffect(() => {
    setIsRendered(true);
    setEdgeToEdge(edgeToEdge);
    return () => {
      setIsRendered(false);
      setEdgeToEdge(false);
    };
  }, [setIsRendered, setEdgeToEdge, edgeToEdge]);
  if (refBound && filterRef.current) {
    return createPortal(<div className={edgeToEdge ? filterRendererEdgeToEdgeBleedCss : filterRendererBleedCss} data-edge-to-edge={edgeToEdge ? 'true' : undefined}>
        <div className={edgeToEdge ? filterRendererEdgeToEdgeContentCss : filterRendererContentCss} data-testid="page-filter-renderer-content">
          {children}
        </div>
      </div>, filterRef.current);
  }
  return null;
};

require("./pageFilterContext.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./pageFilterContext.tsx");